import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import {
  CurrencySelect,
  CURRENCY_NO_CONVERSION_NOTE,
} from '@/app/components/currency-select';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { useVendors, useCreatePurchaseOrder } from '@/hooks/use-procurement';
import { useProjects } from '@/hooks/use-projects';
import { useOrgCurrency } from '@/hooks/use-settings';
import { handleApiError } from '@/lib/api-client';
import { formatMoney } from '@/lib/currency';
import type { CreatePurchaseOrderLinePayload } from '@/lib/types';

interface LineDraft {
  description: string;
  sku: string;
  quantity: string;
  unit_cost: string;
  tax_rate: string;
}

const blankLine = (): LineDraft => ({
  description: '',
  sku: '',
  quantity: '1',
  unit_cost: '0',
  tax_rate: '0',
});

export function PurchaseOrderCreatePage() {
  const navigate = useNavigate();
  const orgCurrency = useOrgCurrency();
  const { data: vendorsData } = useVendors({ page_size: 200, status: 'active' });
  const createPO = useCreatePurchaseOrder();
  const [searchParams] = useSearchParams();
  const { data: projectsData } = useProjects({ status: 'active', page_size: 1000 });

  const [form, setForm] = useState({
    po_number: '',
    vendor: '',
    project: searchParams.get('project') ?? '',
    currency: '',
    expected_delivery: '',
    payment_terms: '',
    notes: '',
  });
  const [lines, setLines] = useState<LineDraft[]>([blankLine()]);
  const [error, setError] = useState<string | null>(null);

  const updateLine = (idx: number, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const addLine = () => setLines((prev) => [...prev, blankLine()]);
  const removeLine = (idx: number) =>
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const subtotal = lines.reduce((sum, l) => {
    const q = Number(l.quantity || 0);
    const c = Number(l.unit_cost || 0);
    return sum + q * c;
  }, 0);
  const tax = lines.reduce((sum, l) => {
    const q = Number(l.quantity || 0);
    const c = Number(l.unit_cost || 0);
    const t = Number(l.tax_rate || 0);
    return sum + (q * c * t) / 100;
  }, 0);
  const total = subtotal + tax;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.po_number.trim()) {
      setError('PO number is required');
      return;
    }
    if (!form.vendor) {
      setError('Vendor is required');
      return;
    }
    const cleanedLines: CreatePurchaseOrderLinePayload[] = lines
      .filter((l) => l.description.trim() !== '')
      .map((l, i) => ({
        line_number: i + 1,
        description: l.description.trim(),
        sku: l.sku.trim(),
        quantity: l.quantity || '0',
        unit_cost: l.unit_cost || '0',
        tax_rate: l.tax_rate || '0',
      }));
    try {
      const po = await createPO.mutateAsync({
        po_number: form.po_number.trim(),
        vendor: Number(form.vendor),
        project: form.project ? Number(form.project) : null,
        currency: form.currency || undefined,
        expected_delivery: form.expected_delivery || null,
        payment_terms: form.payment_terms,
        notes: form.notes,
        lines: cleanedLines,
      });
      navigate(`/purchase-orders/${po.id}`);
    } catch (err) {
      const apiErr = handleApiError(err);
      const fieldMsg = apiErr.errors
        ? Object.entries(apiErr.errors)
            .map(([field, msgs]) => `${field}: ${msgs[0]}`)
            .join('; ')
        : '';
      setError(fieldMsg || apiErr.message || 'Failed to create purchase order');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/purchase-orders">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Link>
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">New Purchase Order</h1>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">PO Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="po_number">PO Number *</Label>
              <Input
                id="po_number"
                value={form.po_number}
                onChange={(e) => setForm((p) => ({ ...p, po_number: e.target.value }))}
                placeholder="PO-1001"
              />
            </div>
            <div>
              <Label htmlFor="vendor">Vendor *</Label>
              <select
                id="vendor"
                value={form.vendor}
                onChange={(e) => {
                  const vendor = (vendorsData?.results ?? []).find(
                    (item) => item.id === Number(e.target.value)
                  );
                  setForm((p) => ({
                    ...p,
                    vendor: e.target.value,
                    currency: vendor?.default_currency || '',
                  }));
                }}
                className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
              >
                <option value="">Select vendor…</option>
                {(vendorsData?.results ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="project">Job (optional)</Label>
              <select
                id="project"
                value={form.project}
                onChange={(e) => setForm((p) => ({ ...p, project: e.target.value }))}
                className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
              >
                <option value="">No job</option>
                {(projectsData?.results ?? []).map((proj) => (
                  <option key={proj.id} value={proj.id}>
                    {proj.project_number ? `${proj.project_number} — ${proj.name}` : proj.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Received costs on this PO count towards the job in Job Costing.
              </p>
            </div>
            <div>
              <Label htmlFor="currency">Currency</Label>
              <CurrencySelect
                id="currency"
                value={form.currency || orgCurrency}
                onValueChange={(value) =>
                  setForm((p) => ({ ...p, currency: value }))
                }
              />
              <p className="mt-1 text-xs text-gray-500">
                {CURRENCY_NO_CONVERSION_NOTE}
              </p>
            </div>
            <div>
              <Label htmlFor="expected_delivery">Expected Delivery</Label>
              <Input
                id="expected_delivery"
                type="date"
                value={form.expected_delivery}
                onChange={(e) =>
                  setForm((p) => ({ ...p, expected_delivery: e.target.value }))
                }
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="payment_terms">Payment Terms</Label>
              <Input
                id="payment_terms"
                value={form.payment_terms}
                onChange={(e) =>
                  setForm((p) => ({ ...p, payment_terms: e.target.value }))
                }
                placeholder="Net 30"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Line Items</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addLine}>
              <Plus className="h-4 w-4 mr-1" />
              Add Line
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {lines.map((line, idx) => (
              <div
                key={idx}
                className="grid grid-cols-12 gap-2 items-end border-b pb-3 last:border-0 last:pb-0"
              >
                <div className="col-span-12 sm:col-span-4">
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={line.description}
                    onChange={(e) =>
                      updateLine(idx, { description: e.target.value })
                    }
                    placeholder="Item description"
                  />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <Label className="text-xs">SKU</Label>
                  <Input
                    value={line.sku}
                    onChange={(e) => updateLine(idx, { sku: e.target.value })}
                  />
                </div>
                <div className="col-span-3 sm:col-span-1">
                  <Label className="text-xs">Qty</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={line.quantity}
                    onChange={(e) =>
                      updateLine(idx, { quantity: e.target.value })
                    }
                  />
                </div>
                <div className="col-span-3 sm:col-span-2">
                  <Label className="text-xs">Unit Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={line.unit_cost}
                    onChange={(e) =>
                      updateLine(idx, { unit_cost: e.target.value })
                    }
                  />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-xs">Tax %</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={line.tax_rate}
                    onChange={(e) =>
                      updateLine(idx, { tax_rate: e.target.value })
                    }
                  />
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLine(idx)}
                    disabled={lines.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            <div className="flex justify-end pt-2">
              <div className="w-64 text-sm space-y-1">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>{formatMoney(subtotal, form.currency || orgCurrency)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Tax</span>
                  <span>{formatMoney(tax, form.currency || orgCurrency)}</span>
                </div>
                <div className="flex justify-between font-semibold text-gray-900 border-t pt-1">
                  <span>Total</span>
                  <span>{formatMoney(total, form.currency || orgCurrency)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" type="button" asChild>
            <Link to="/purchase-orders">Cancel</Link>
          </Button>
          <Button type="submit" disabled={createPO.isPending}>
            {createPO.isPending ? 'Creating…' : 'Create Purchase Order'}
          </Button>
        </div>
      </form>
    </div>
  );
}
